/**
 * TELV1-655 — rescue for a reply stream that is held on the respondent's
 * network path.
 *
 * The worker answers `PUT /chat/:id` as an SSE stream and writes
 * MESSAGE_RECEIVED within one round trip (text and media alike), the reply
 * ~1 s later, then closes. On some networks (endpoint web shields, ISP or
 * carrier middleboxes) the response headers reach the browser but the body is
 * held back, so the respondent stared at the typing dots until the 45-60 s
 * fallback timers fired, followed by a "No response detected" toast.
 *
 * Once the stream has been silent for STREAM_STALL_MS after its headers, the
 * chat checks the conversation history every RESCUE_POLL_MS and shows the
 * reply as soon as the worker has saved it.
 *
 * This covers a fully held body only: the first parsed event disarms the
 * rescue, because healthy media turns legitimately go quiet after it
 * (transcription, AI call). A path that passes MESSAGE_RECEIVED and then holds
 * the rest still falls back to the existing timers.
 */

// A healthy stream emits its first event within one round trip of the
// headers, so this much silence means the body is being held, not that the
// worker is still busy (transcription runs after MESSAGE_RECEIVED).
export const STREAM_STALL_MS = 5000;

export const RESCUE_POLL_MS = 3000;

/**
 * Returns the reply to the answer just sent once the server-side history
 * holds it, or null.
 *
 * Strict on purpose: a wrong match would show a stale question and drop a
 * request the worker may still be processing. The history is anchored on its
 * newest message that is already on screen; after that anchor it may hold
 * only answers (at least as many as are on screen after it, including the
 * one just sent) followed by exactly one new assistant message. Anything
 * else (e.g. a turn that ended in an error and left the screen and the
 * server out of step) returns null and leaves recovery to the existing
 * timers.
 */
export const findRescuedReply = (
  history: TMessageData[],
  shownBeforeSend: TMessageData[],
): TMessageData | null => {
  const reply = history[history.length - 1];
  if (!reply || reply.role !== 'assistant' || !reply._id) return null;

  const shownIds = new Set(shownBeforeSend.map((message) => message._id));
  if (shownIds.has(reply._id)) return null;

  let anchor = history.length - 2;
  while (anchor >= 0 && !shownIds.has(history[anchor]._id)) anchor -= 1;
  if (anchor < 0) return null;

  const savedBetween = history.slice(anchor + 1, -1);
  if (savedBetween.some((message) => message.role !== 'user')) return null;

  const anchorOnScreen = shownBeforeSend.findIndex(
    (message) => message._id === history[anchor]._id,
  );
  const answersOnScreen = shownBeforeSend
    .slice(anchorOnScreen + 1)
    .filter((message) => message.role === 'user').length;
  if (savedBetween.length < Math.max(1, answersOnScreen)) return null;

  return reply;
};

interface HeldStreamRescueOptions {
  /** Fetches the conversation history; resolves null when it could not be read. */
  fetchHistory: (signal: AbortSignal) => Promise<TMessageData[] | null>;
  shownBeforeSend: TMessageData[];
  /** False once the respondent has moved to another conversation (chat restart). */
  isSameConversation: () => boolean;
  /** Called at most once, after the rescue has already stopped itself. */
  onReply: (reply: TMessageData, attempts: number) => void;
}

/**
 * Arms the rescue once the response headers are in. Returns stop(), which
 * every other way the turn can end must call; it also aborts a check that is
 * still in flight. Checks are chained (the next one is scheduled only after
 * the previous one finished) because on affected networks a single history
 * request can take ~10 s.
 */
export const armHeldStreamRescue = ({
  fetchHistory,
  shownBeforeSend,
  isSameConversation,
  onReply,
}: HeldStreamRescueOptions): (() => void) => {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempts = 0;
  const inFlight = new AbortController();

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    inFlight.abort();
  };

  const check = async () => {
    if (stopped) return;
    attempts += 1;
    try {
      const history = await fetchHistory(inFlight.signal);
      if (stopped) return;
      if (!isSameConversation()) {
        stop();
        return;
      }
      const reply = history ? findRescuedReply(history, shownBeforeSend) : null;
      if (reply) {
        stop();
        onReply(reply, attempts);
        return;
      }
    } catch {
      // A failed check changes nothing: check again, and the caller's
      // timers stay the last resort.
    }
    if (!stopped) timer = setTimeout(check, RESCUE_POLL_MS);
  };

  timer = setTimeout(check, STREAM_STALL_MS);
  return stop;
};
